import { useState } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';
import PrometheusPageIcon from '../../assets/prometheus-in-linkedin.png';
import messages from '../../ProfilePage.messages';

const LinkedInIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="tw-shrink-0"
  >
    <rect width="24" height="24" rx="4" fill="#0288d1" />
    <path d="M7.5 9.5H5V18H7.5V9.5Z" fill="white" />
    <circle cx="6.25" cy="6.75" r="1.5" fill="white" />
    <path
      d="M19 18H16.5V13.5C16.5 12.4 15.85 11.75 14.875 11.75C13.9 11.75 13.25 12.4 13.25 13.5V18H10.75V9.5H13.25V10.75C13.75 9.9 14.75 9.25 15.875 9.25C17.65 9.25 19 10.55 19 12.75V18Z"
      fill="white"
    />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={`tw-shrink-0 tw-transition-transform tw-duration-300 ${open ? 'tw-rotate-180' : 'tw-rotate-0'}`}
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="#7a00ad"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LinkedInCertificatesBlock = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { formatMessage } = useIntl();

  const bold = chunks => <span className="tw-font-medium">{chunks}</span>;
  const code = chunks => (
    <i className="tw-bg-gray-100 tw-px-1 tw-py-0.5 tw-rounded tw-text-xs">{chunks}</i>
  );
  const urlSpan = chunks => (
    <span className="tw-text-orange-500 tw-inline-block tw-text-ellipsis tw-overflow-hidden tw-w-full tw-whitespace-nowrap md:tw-inline">
      {chunks}
    </span>
  );

  const steps = [
    {
      id: 1,
      title: formatMessage(messages.certificateStep1Title),
      content: (
        <div className="tw-text-sm tw-leading-relaxed">
          <p className="!tw-mb-2">{formatMessage(messages.certificateStep1Intro, { bold })}</p>
          <ul className="tw-pl-4 tw-flex tw-flex-col tw-gap-1.5 tw-list-disc">
            <li>{formatMessage(messages.certificateStep1CertId, { bold, code })}</li>
            <li>{formatMessage(messages.certificateStep1Url, { bold, urlSpan })}</li>
          </ul>
        </div>
      ),
    },
    {
      id: 2,
      title: formatMessage(messages.certificateStep2Title),
      content: (
        <div className="tw-text-sm tw-leading-relaxed">
          <p className="!tw-mb-2">{formatMessage(messages.certificateStep2Intro, { bold })}</p>
          <ul className="tw-pl-4 tw-flex tw-flex-col tw-gap-1.5 tw-list-disc tw-mb-3">
            <li>{formatMessage(messages.certificateStep2Name, { bold })}</li>
            <li>
              {formatMessage(messages.certificateStep2Org, { bold })}
              <img
                src={`${PrometheusPageIcon}`}
                className="tw-w-[200px] tw-h-auto md:tw-w-[300px]"
                alt="Prometheus search image example"
              />
            </li>
            <li>{formatMessage(messages.certificateStep2CertId, { bold, code })}</li>
            <li>{formatMessage(messages.certificateStep2VerificationUrl, { bold, urlSpan })}</li>
          </ul>
        </div>
      ),
    },
    {
      id: 3,
      title: formatMessage(messages.certificateStep3Title),
      content: (
        <div className="tw-text-sm tw-leading-relaxed">
          <ul className="tw-pl-4 tw-flex tw-flex-col tw-gap-1.5 tw-list-disc tw-mb-3">
            <li>{formatMessage(messages.certificateStep3Media, { bold })}</li>
            <li>{formatMessage(messages.certificateStep3Skills, { bold })}</li>
          </ul>
          <p className="!tw-mb-2">{formatMessage(messages.certificateStep3Save, { bold })}</p>
          <p className="tw-font-medium">{formatMessage(messages.certificateStep3Tag)}</p>
        </div>
      ),
    },
  ];

  return (
    <div className="tw-mt-10 tw-pr-6 md:tw-pr-12 lg:tw-mt-12 xl:tw-pr-[80px]">
      <div className="tw-bg-white tw-rounded-xl tw-overflow-hidden tw-transition-shadow tw-duration-300 tw-shadow-lg tw-shadow-body tw-border-[1px] tw-border-mainPurple">
        {/* Accordion trigger */}
        <button
          type="button"
          onClick={() => setIsOpen(prev => !prev)}
          className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-left tw-gap-3"
        >
          <div className="tw-flex tw-items-center tw-gap-4">
            <LinkedInIcon />
            <p className="tw-m-0 tw-text-sm tw-leading-snug tw-font-medium md:tw-text-base">
              {formatMessage(messages.certificateInstructionsTitle)}
            </p>
          </div>
          <ChevronIcon open={isOpen} />
        </button>

        {/* Accordion content */}
        <div
          className={`tw-overflow-hidden tw-transition-all tw-duration-300 ${isOpen ? 'tw-max-h-[fit-content]' : 'tw-max-h-0'}`}
        >
          <div className="tw-px-5 tw-pb-6 tw-flex tw-flex-col tw-gap-4 md:tw-gap-6">
            {steps.map(step => (
              <div key={step.id}>
                {/* Step header */}
                <div className="tw-flex tw-gap-2 tw-mb-2">
                  <span className="tw-font-medium">{step.id}.</span>
                  <span className="tw-font-medium">{step.title}</span>
                </div>
                {/* Step content */}
                <div className="tw-pl-4">{step.content}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedInCertificatesBlock;
